import { getApiBaseUrl } from '../config/api';

export interface AvailabilityWindow {
  dayOfWeek: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

export interface AvailabilitySummary {
  caretakerId: string;
  date: string;
  isAvailableOnDate: boolean;
  isGenerallyAvailable: boolean;
  sameDayAvailable: boolean;
  nextAvailableDate: string | null;
}

export interface AvailabilitySlot {
  id: string;
  caretakerId: string;
  date: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  isBooked: boolean;
  appointmentId: string | null;
}

class AvailabilityService {
  private createIdempotencyKey(prefix: string): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private getAuthHeaders(includeIdempotency: boolean = false): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(includeIdempotency && { 'Idempotency-Key': this.createIdempotencyKey('web-availability') }),
    };
  }

  async upsertMyWindows(windows: AvailabilityWindow[]): Promise<AvailabilityWindow[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/availability/my/windows`, {
      method: 'PUT',
      headers: this.getAuthHeaders(true),
      body: JSON.stringify({ windows }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save availability windows');
    }

    const result = await response.json();
    return result.data;
  }

  async getSummary(caretakerId: string, date?: string): Promise<AvailabilitySummary> {
    const baseUrl = await getApiBaseUrl();
    const params = new URLSearchParams();
    if (date) {
      params.set('date', date);
    }

    const query = params.toString();
    const response = await fetch(
      `${baseUrl}/availability/summary/${caretakerId}${query ? `?${query}` : ''}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to load availability summary');
    }

    const result = await response.json();
    return result.data;
  }

  async getSlots(caretakerId: string, date: string): Promise<AvailabilitySlot[]> {
    const baseUrl = await getApiBaseUrl();
    const params = new URLSearchParams({ caretakerId, date });
    const response = await fetch(`${baseUrl}/availability/slots?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to load availability slots');
    }

    const result = await response.json();
    return result.data;
  }
}

export const availabilityService = new AvailabilityService();
